import React, { useMemo, useCallback } from "react";
import { selectionToUserEntity } from "./utils";
import { SelectionEntityTrigger } from "./selection-entity-trigger";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";
import { useMemberSelection } from "./use-member-selection";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

export type UserSelectorProps = Omittable<{
  /** Currently selected user ID(s) */
  value?: string | string[] | null;
  /** Called when selection changes with an array of user IDs */
  onValueChange?: (ids: string[]) => void;
  /** User IDs to hide from the pool */
  exclude?: string[];
  /** Restrict the pool to active members only (excludes invited/pending). */
  activeOnly?: boolean;
  /** The member statuses to list, in place of `activeOnly`'s choice. */
  statuses?: readonly string[];
  /** Maximum number of selections (1 for single-select) */
  maxSelections?: number;
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  /** `"list"` seats the selected members in their own section above the
   *  pool, the assignee picker's list grammar. */
  mode?: "pool" | "list";
  /** The phone sheet's caption — the property this picks for ("Reports to",
   *  a member field's name). Default "Members". */
  title?: string;
  placeholder?: string;
  size?: SelectorSize;
  /** Corners of the default (non-chips) trigger — "square" for row chrome. */
  shape?: React.ComponentProps<typeof SelectionEntityTrigger>["shape"];
  disabled?: boolean;
  /** Render selections as removable chips in the trigger. */
  chips?: boolean;
  /** Caret on the default (non-chips) trigger — the picker-affordance hint. */
  showChevron?: boolean;
  /** Leading icon shown in the trigger. */
  icon?: IconName;
  /** Stretch trigger to fill its container. */
  fill?: boolean;
  field?: boolean;
  /** Match popover width to trigger. */
  contentFit?: boolean;
  /** Popover min-width preset. */
  contentSize?: EntitySelectorContentSize;
  /** Controlled open state. */
  open?: boolean;
  defaultOpen?: boolean;
  /** Called when the selector opens or closes */
  onOpenChange?: (open: boolean) => void;
  /** Rendered under the pool in the default content (an `EntitySelectorFooter`). */
  footer?: React.ReactNode;
  /** Override the default trigger + content. Receives EntitySelector context. */
  children?: React.ReactNode;
}>;

/**
 * Domain wrapper around EntitySelector for selecting workspace users.
 * Handles data fetching, item mapping, and value conversion internally.
 */
export function UserSelector({
  value,
  onValueChange,
  exclude,
  activeOnly,
  statuses,
  maxSelections,
  variant,
  mode = "pool",
  placeholder = "Select members...",
  title = "Members",
  size,
  shape,
  disabled,
  chips,
  showChevron,
  icon,
  fill,
  field,
  contentFit,
  contentSize,
  open,
  defaultOpen,
  onOpenChange,
  footer,
  children,
}: UserSelectorProps) {
  const { pool, selections, toIds } = useMemberSelection({ value, exclude, activeOnly, statuses });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections,
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
      noItemsMessage: "No members yet",
    }),
    [pool, maxSelections],
  );

  // One pick replaces the last; a multi-select carries the picks it cannot
  // show yet (`toIds`).
  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      onValueChange?.(maxSelections === 1 ? next.map((s) => s.id) : toIds(next));
    },
    [onValueChange, maxSelections, toIds],
  );

  return (
    <EntitySelector
      variant={variant}
      config={config}
      value={selections}
      onValueChange={handleValueChange}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      title={title}
    >
      {children ?? (
        <>
          {chips ? (
            <EntitySelectorTrigger
              placeholder={placeholder}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
              chips
              fill={fill}
              icon={icon}
            />
          ) : (
            <SelectionEntityTrigger
              toEntity={selectionToUserEntity}
              placeholder={placeholder}
              title="Members"
              emptyIcon={icon}
              size={size}
              shape={shape}
              disabled={disabled}
              fill={fill}
              field={field}
              showChevron={showChevron}
            />
          )}
          <EntitySelectorContent size={contentSize} fit={contentFit}>
            <EntitySelectorInput />
            {mode === "list" ? (
              <EntitySelectorSelected
                label={title}
                hideWhenEmpty
                hideBadge
                hideSelectionHighlight
              />
            ) : null}
            <EntitySelectorPool
              value={pool.id}
              hideSelectionHighlight={mode === "list"}
            />
            {footer}
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
